import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Sub, Table } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { fetchGoalDetail, formatNumber, mergeSeries } from '../config';

// E04 orta sağ: zaman serisi gerçekleşme — hedef bazında plan ve gerçekleşen değer. Gerçekleşen noktalar yalnız
// güncelleme kayıtlarından; veri noktası olmayan tarihte gerçekleşen çizgisi KESİLİR (connectNulls=false; plan eğrisi
// basamaklı bir tanım olduğu için birleştirilir). Plan eğrisi yalnız tarihli
// adımı olan kilometre taşında vardır; yoksa açıkça "plan eğrisi yok" yazılır (tahmin üretilmez).
export default function GoalSeriesPanel({ filters, selected }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [table, setTable] = useState(false);
  const goalId = selected?.goalId;
  const panel = usePanel(() => fetchGoalDetail(filters, goalId), [JSON.stringify(filters), goalId], !!filters.periodId && !!goalId);
  const data = mergeSeries(panel.data?.actual, panel.data?.plan);
  const title = selected?.title
    ? t('performance.goalAnalytics.panel.seriesFor', { title: selected.title })
    : t('performance.goalAnalytics.panel.series');

  if (!goalId) {
    return (
      <PanelCard panelKey="goalSeries" title={title} span={5} empty emptyText={t('performance.goalAnalytics.panel.selectGoal')} />
    );
  }

  return (
    <PanelCard
      panelKey="goalSeries"
      title={title}
      span={5}
      loading={panel.loading}
      error={panel.error}
      onRetry={panel.retry}
      empty={data.length === 0}
      emptyText={t('performance.goalAnalytics.panel.noSeries')}
      tableToggle
      showTable={table}
      onToggleTable={() => setTable((v) => !v)}
    >
      {table ? (
        <Table>
          <thead>
            <tr>
              <th>{t('performance.goalAnalytics.col.date')}</th>
              <th>{t('performance.goalAnalytics.series.plan')}</th>
              <th>{t('performance.goalAnalytics.series.actual')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((p) => (
              <tr key={p.day} data-row={p.day}>
                <td>{p.day}</td>
                <td>{p.plan != null ? `${formatNumber(p.plan, i18n.language)}%` : '-'}</td>
                <td>{p.actual != null ? `${formatNumber(p.actual, i18n.language)}%` : '-'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: theme.metrics.font.size.xs }} />
              <YAxis tick={{ fontSize: theme.metrics.font.size.xs }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: theme.metrics.font.size.xs }} />
              {panel.data?.hasPlan ? (
                <Line type="stepAfter" dataKey="plan" name={t('performance.goalAnalytics.series.plan')} stroke={theme.colors.text.muted} strokeDasharray="5 5" dot connectNulls />
              ) : null}
              <Line type="linear" dataKey="actual" name={t('performance.goalAnalytics.series.actual')} stroke={theme.colors.primary.main} dot connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {!panel.data?.hasPlan ? <Sub>{t('performance.goalAnalytics.panel.noPlan')}</Sub> : null}
    </PanelCard>
  );
}
